/**
 * API server pin for P1_VP12.
 * Priority: ?api=… → localStorage → window.ApiServer → /bestbuytest
 *
 * Browser + Vite: use proxy paths (/bestbuytest).
 * Packaged Electron (file://): map to absolute https origins (no Vite proxy).
 */

const STORAGE_KEY = 'p1vp12.apiServer'

export const API_PRESETS = [
  { path: '/bestbuytest', absolute: 'https://bestbuytest.kloudp1.com', label: 'BestBuy Test Server' },
  { path: '/jiaxing', absolute: 'https://jiaxing.devsuite.cn', label: 'Jiaxing Server' }
]

const ABSOLUTE_BY_PATH = API_PRESETS.reduce((acc, p) => {
  acc[p.path] = p.absolute
  return acc
}, {})

/** Packaged desktop has no Vite proxy — use absolute API hosts. */
export function needsAbsoluteApi () {
  try {
    if (typeof location !== 'undefined' && location.protocol === 'file:') return true
    const desk = typeof window !== 'undefined' && window.p1Desktop
    if (desk && desk.forceAbsoluteApi) return true
    const host = (typeof location !== 'undefined' && location.hostname) || ''
    if (desk && host && host !== 'localhost' && host !== '127.0.0.1') return true
  } catch (e) { /* ignore */ }
  return false
}

export function resolveApiRoot (pathOrUrl) {
  const raw = (pathOrUrl || '').trim() || '/bestbuytest'
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/$/, '')
  if (!needsAbsoluteApi()) return raw.replace(/\/$/, '') || '/bestbuytest'
  const key = raw.startsWith('/') ? raw : `/${raw}`
  const abs = ABSOLUTE_BY_PATH[key] ||
    (typeof window !== 'undefined' &&
      window.p1Desktop &&
      window.p1Desktop.apiOrigins &&
      window.p1Desktop.apiOrigins[key])
  return String(abs || raw).replace(/\/$/, '')
}

export function getApiServer () {
  return resolveApiRoot(window.ApiServer || '/bestbuytest')
}

function applyApiRoots (root) {
  const resolved = resolveApiRoot(root)
  window.ApiServer = resolved
  window.DevSpecApi = resolved + '/DevSpecAPI/'
  window.DevTrackApi = resolved + '/DevTrackApi/api/'
  window.DevTestApi = resolved + '/DevTestApi/api/'
  window.P1AdminApi = resolved + '/P1AdminAPI/api/'
  if (typeof globalThis !== 'undefined') {
    globalThis.DevSpecApi = window.DevSpecApi
    globalThis.DevTrackApi = window.DevTrackApi
  }
}

export function setApiServer (path) {
  const next = (path || '').trim() || '/bestbuytest'
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch (e) {}
  applyApiRoots(next)
  return getApiServer()
}

export function initApiConfig () {
  const params = new URLSearchParams(window.location.search || '')
  const fromQuery = params.get('api')
  let fromStorage = null
  try {
    fromStorage = localStorage.getItem(STORAGE_KEY)
  } catch (e) {}

  if (fromQuery) {
    setApiServer(fromQuery)
  } else if (fromStorage) {
    setApiServer(fromStorage)
  } else if (!window.ApiServer) {
    setApiServer('/bestbuytest')
  } else {
    applyApiRoots(window.ApiServer)
  }
}

/** Probe that the API root responds (proxy or absolute). */
export async function probeApiServer () {
  const base = getApiServer().replace(/\/$/, '')
  const url = `${base}/`
  const started = Date.now()
  try {
    const res = await fetch(url, { method: 'GET', credentials: 'include' })
    return {
      ok: res.status > 0,
      status: res.status,
      ms: Date.now() - started,
      url,
      message: res.status >= 200 && res.status < 500
        ? (needsAbsoluteApi() ? 'Reachable (absolute API)' : 'Reachable via proxy')
        : `HTTP ${res.status}`
    }
  } catch (e) {
    return {
      ok: false,
      status: 0,
      ms: Date.now() - started,
      url,
      message: e.message || String(e)
    }
  }
}
